import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Popover,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppButton, AppIconButton } from '../design-system';
import { Lock, AccessTime, ExpandMore } from '@mui/icons-material';
import { useEfemLockContext } from '../../context/EfemLockContext';
import { efemService } from '../../services/EfemService';
import { PRODUCT_NAMES, AXON_LOCK_BANNER_TITLE, AXON_LOCK_LABEL } from '@cnapse/shared';
import { efemLockTimeoutSeconds } from '../../config/efem-lock-config';
import { getAccentSurfaceColors, getLockBannerChromeColors } from '../../theme/uiSemanticColors';

// Pages that require EFEM lock - if user is on any of these, redirect after release
const EFEM_REQUIRED_PAGES = [
  '/operations/jobs/new',
  '/operations/wafer-id-acquire',
  '/wafer-transfer',
];

/**
 * Check if current path requires EFEM lock
 */
const requiresEfemLock = (pathname: string): boolean => {
  return EFEM_REQUIRED_PAGES.some(page => pathname.startsWith(page));
};

/**
 * Global EFEM Lock Indicator - Persists across all pages
 * Shows lock status from global context, rendered in the toolbar
 */
export const GlobalEfemLockIndicator: React.FC = () => {
  const theme = useTheme();
  const lockChrome = useMemo(() => getLockBannerChromeColors(theme), [theme]);
  const accentSurfaces = useMemo(() => getAccentSurfaceColors(theme), [theme]);
  const { globalLock, setGlobalLock, clearGlobalLock } = useEfemLockContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [extending, setExtending] = useState(false);
  const [shouldRedirectOnClear, setShouldRedirectOnClear] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle redirect when lock is cleared
  useEffect(() => {
    if (!globalLock && shouldRedirectOnClear) {
      setShouldRedirectOnClear(false);
      // Use setTimeout to ensure state updates have completed
      setTimeout(() => {
        // Use both navigation methods to ensure compatibility
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
        navigate('/', { replace: true });
      }, 0);
    }
  }, [globalLock, shouldRedirectOnClear, navigate]);

  // Calculate time remaining
  useEffect(() => {
    if (!globalLock?.expiresAt) {
      setTimeRemaining(0);
      return;
    }

    const updateTimeRemaining = () => {
      const now = new Date();
      const expiry = new Date(globalLock.expiresAt!);
      const secondsLeft = Math.max(0, Math.floor((expiry.getTime() - now.getTime()) / 1000));
      setTimeRemaining(secondsLeft);

      // Clear lock if expired
      if (secondsLeft <= 0) {
        // Set redirect flag BEFORE clearing if on EFEM page
        if (requiresEfemLock(window.location.pathname)) {
          setShouldRedirectOnClear(true);
        }
        clearGlobalLock();
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [globalLock?.expiresAt, clearGlobalLock, navigate, location.pathname]);

  const handleExtendLock = async () => {
    if (!globalLock?.gatewaySerialNumber) return;

    setExtending(true);
    try {
      const response = await efemService.extendEfemLock(globalLock.gatewaySerialNumber);
      if (response.success && response.data?.expiresAt) {
        // Update global context with new expiry
        setGlobalLock({
          ...globalLock,
          expiresAt: new Date(response.data.expiresAt),
        });
      }
    } catch (error) {
      console.error('Failed to extend lock:', error);
    } finally {
      setTimeout(() => setExtending(false), 500);
    }
  };

  const handleReleaseLock = async () => {
    if (!globalLock?.gatewaySerialNumber) return;

    setShowReleaseConfirm(false);
    
    // Check if user is CURRENTLY on a page that requires EFEM lock
    // Use window.location.pathname to get the actual current URL (more reliable than useLocation)
    const currentPath = window.location.pathname;
    const shouldRedirect = requiresEfemLock(currentPath);
    
    // Set flag BEFORE clearing lock if we need to redirect
    if (shouldRedirect) {
      setShouldRedirectOnClear(true);
    }
    
    try {
      await efemService.releaseEfemLock(globalLock.gatewaySerialNumber);
      clearGlobalLock();
    } catch (error) {
      console.error('Failed to release lock:', error);
      // Still clear from UI even if API fails
      clearGlobalLock();
    }
  };

  // Don't render if no active lock
  if (!globalLock?.gatewaySerialNumber || !globalLock?.clientName) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isExpiringSoon = timeRemaining < 300; // Less than 5 minutes
  const isCritical = timeRemaining < 120; // Less than 2 minutes
  const progressPercent = Math.min(100, Math.max(0, (timeRemaining / efemLockTimeoutSeconds) * 100));
  const extensionMinutes = Math.round(efemLockTimeoutSeconds / 60);
  
  const bgColor = isCritical
    ? theme.palette.error.main
    : isExpiringSoon
      ? theme.palette.warning.main
      : theme.palette.primary.main;

  return (
    <>
      <Tooltip title={`${AXON_LOCK_LABEL} active: ${globalLock.clientName}`}>
        <AppIconButton
          onClick={handleClick}
          sx={{
            bgcolor: bgColor,
            color: 'white',
            width: 40,
            height: 40,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: bgColor,
              filter: 'brightness(0.95)',
              transform: 'scale(1.05)',
            },
            ml: 1,
          }}
        >
          <Lock sx={{ fontSize: 20 }} />
        </AppIconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: bgColor,
            color: 'white',
            width: 320,
            mt: 1.5,
            boxShadow: lockChrome.dialogShadow,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Lock sx={{ fontSize: 20, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, letterSpacing: '0.5px' }}>
                {AXON_LOCK_BANNER_TITLE}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {globalLock.clientName}
              </Typography>
              {globalLock.operation && (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Operation: {globalLock.operation}
                </Typography>
              )}
            </Box>
            <AppIconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'white', mt: -0.5, mr: -0.5 }}
            >
              <ExpandMore />
            </AppIconButton>
          </Stack>
          
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
            <AccessTime sx={{ fontSize: 16 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
              {formatTime(timeRemaining)}
            </Typography>
          </Stack>
          
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              mt: 1.5,
              height: 4,
              borderRadius: 2,
              bgcolor: lockChrome.glassBg,
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white',
                borderRadius: 2,
              }
            }}
          />
          
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <AppButton
              variant="contained"
              size="small"
              fullWidth
              onClick={handleExtendLock}
              disabled={extending}
              sx={{
                bgcolor: 'white',
                color: bgColor,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: lockChrome.progressTrack,
                },
                '&.Mui-disabled': {
                  bgcolor: lockChrome.progressBar,
                  color: bgColor,
                }
              }}
            >
              {extending ? 'Extending...' : `Extend +${extensionMinutes} min`}
            </AppButton>
            
            <AppButton
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => {
                setShowReleaseConfirm(true);
                handleClose();
              }}
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: lockChrome.progressBarMuted,
                }
              }}
            >
              Release Lock
            </AppButton>
          </Stack>
        </Box>
      </Popover>

      {/* Release confirmation dialog */}
      <Dialog
        open={showReleaseConfirm}
        onClose={() => setShowReleaseConfirm(false)}
        maxWidth="sm"
        fullWidth
        sx={(theme) => ({
          '& .MuiDialog-paper': {
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none !important',
          }
        })}
      >
        <DialogTitle sx={{ pb: 1 }}>Release {AXON_LOCK_LABEL}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to release the lock on <strong>{globalLock.clientName}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            This will allow other users to control this {PRODUCT_NAMES.AXON}.
          </DialogContentText>
          {requiresEfemLock(window.location.pathname) && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: accentSurfaces.warningTintBg,
                borderRadius: 1,
                border: '1px solid',
                borderColor: accentSurfaces.warningBorder,
              }}
            >
              <Typography 
                variant="body2" 
                sx={(theme) => ({ 
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.warning.light
                    : 'warning.dark',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                })}
              >
                <Box component="span" sx={{ fontSize: '1.2em' }}>⚠️</Box>
                You will be redirected to the home page after releasing the lock.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <AppButton onClick={() => setShowReleaseConfirm(false)} color="inherit">
            Cancel
          </AppButton>
          <AppButton onClick={handleReleaseLock} color="error" variant="contained">
            Release Lock
          </AppButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
